const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW", "PYG",
  "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);

const THREE_DECIMAL_CURRENCIES = new Set([
  "BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND",
]);

export function canonicalCurrencyCode(currency: string): string {
  const code = currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) throw new RangeError(`Invalid ISO currency code: ${currency}`);
  try {
    new Intl.NumberFormat("en", { style: "currency", currency: code });
  } catch {
    throw new RangeError(`Invalid ISO currency code: ${currency}`);
  }
  return code;
}

export function formatMinorUnits(amount: number, currency: string, locale?: string): string {
  const code = canonicalCurrencyCode(currency);
  const minorDigits = minorDigitsFor(code);
  const major = amount / 10 ** minorDigits;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: code }).format(major);
  } catch {
    return `${code} ${major.toFixed(minorDigits)}`;
  }
}

export function minorDigitsFor(currency: string): number {
  const code = canonicalCurrencyCode(currency);
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
  return 2;
}

export function parseMajorToMinor(value: string, currency: string): number {
  const digits = minorDigitsFor(currency);
  const match = /^\+?(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) throw new RangeError("Amount must be a positive decimal");
  const fraction = match[2] ?? "";
  if (fraction.length > digits) throw new RangeError(`${currency.toUpperCase()} supports ${digits} decimal places`);
  const minor = BigInt(match[1] ?? "0") * 10n ** BigInt(digits)
    + BigInt(fraction.padEnd(digits, "0") || "0");
  const result = Number(minor);
  if (!Number.isSafeInteger(result)) throw new RangeError("Amount exceeds safe integer range");
  return result;
}

/** Normalize a positive, non-exponent decimal for storage in DECIMAL(36,18). */
export function canonicalDecimalString(value: string): string {
  const match = /^\+?(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) throw new RangeError("FX rate must be a positive decimal string");
  const integer = (match[1] ?? "0").replace(/^0+(?=\d)/, "");
  const fraction = (match[2] ?? "").replace(/0+$/, "");
  if (fraction.length > 18 || integer.length > 18 || integer.length + fraction.length > 36) {
    throw new RangeError("FX rate exceeds DECIMAL(36,18)");
  }
  if (BigInt(integer + fraction) === 0n) throw new RangeError("FX rate must be greater than zero");
  return fraction ? `${integer}.${fraction}` : integer;
}

/**
 * Convert minor units using integer arithmetic and round half away from zero.
 * rate is target major units per one source major unit.
 */
export function convertMinorUnits(
  amount: number,
  sourceCurrency: string,
  targetCurrency: string,
  rate: string,
): number {
  if (!Number.isSafeInteger(amount)) throw new RangeError("Amount must be a safe integer");
  const normalizedRate = canonicalDecimalString(rate);
  const [whole = "0", fraction = ""] = normalizedRate.split(".");
  const scale = 10n ** BigInt(fraction.length);
  const rateInteger = BigInt(whole + fraction);
  const numerator = BigInt(amount) * rateInteger * 10n ** BigInt(minorDigitsFor(targetCurrency));
  const denominator = scale * 10n ** BigInt(minorDigitsFor(sourceCurrency));
  const absolute = numerator < 0n ? -numerator : numerator;
  const rounded = (absolute + denominator / 2n) / denominator;
  const result = numerator < 0n ? -rounded : rounded;
  const converted = Number(result);
  if (!Number.isSafeInteger(converted)) throw new RangeError("Converted amount exceeds safe integer range");
  return converted;
}
